/**
 * Common Environment Configuration
 *
 * This file defines the standard environments used across all applications
 * in the mono-learnbahn monorepo.
 *
 * Usage:
 * - Frontend/Web apps: Import this file to get type-safe environment enums
 * - Set ENVIRONMENT env var to one of these values
 */

export enum Environment {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
}

/**
 * Default timezone used across the application.
 * All timestamps are stored and displayed in IST by default.
 * Clients can convert to local timezone using this offset info.
 */
export const APP_TIMEZONE = 'Asia/Kolkata';

/**
 * Valid environment strings that can be set via environment variables
 */
export type EnvironmentString = 'production' | 'development';

/**
 * Parse environment string and return typed Environment enum
 * Falls back to DEVELOPMENT if invalid value provided
 */
export function parseEnvironment(env: string | undefined): Environment {
  const normalized = env?.toLowerCase().trim();

  switch (normalized) {
    case 'production':
      return Environment.PRODUCTION;
    case 'development':
      return Environment.DEVELOPMENT;
    default:
      console.warn(`Unknown environment "${env}", defaulting to DEVELOPMENT`);
      return Environment.DEVELOPMENT;
  }
}

/**
 * Type guard to check if a string is a valid environment
 */
export function isValidEnvironment(env: string): env is EnvironmentString {
  return env === 'production' || env === 'development';
}

/**
 * Get current environment from process.env
 * Supports both ENVIRONMENT and NODE_ENV variables
 * ENVIRONMENT takes precedence
 */
export function getCurrentEnvironment(): Environment {
  const envVar = process.env.ENVIRONMENT || process.env.NODE_ENV;
  return parseEnvironment(envVar);
}

/**
 * Helper to check if running in production
 */
export function isProduction(): boolean {
  return getCurrentEnvironment() === Environment.PRODUCTION;
}

/**
 * Helper to check if running in development
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironment() === Environment.DEVELOPMENT;
}
